import { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  apiConteosEscaneoCrear,
  apiConteosEscaneoCrearLote,
  apiConteosEscaneoCatalogoInventario,
  apiConteosEscaneoListar,
  apiConteosEscaneoZonas,
  apiConteosEscaneoEliminar,
  apiConteosEscaneoAbrir,
  apiConteosEscaneoCerrar,
  apiConteosEscaneoReiniciar,
  apiConteosEscaneoEstado,
  apiConteosEscaneoStats,
  apiConteosEscaneoDisponible,
  apiConteosEscaneoEliminarConteoCompleto,
  apiBuscarArticuloPorCodigoBarras,
  apiBuscarArticuloPorClaveMedida,
  apiBuscarArticulosInventario,
  apiListaAlmacenesExhibidores,
  apiTomaInventarioActivo,
} from "../../settings/ApiConfig";
import { useRequest } from "../System/RequestContext";
import { useSesion } from "../System/SesionContext";
import { useLayout } from "../System/LayoutContext";
import { getAuthorizationToken } from "../../settings/sessionStorage";

export const ConteosEscaneoContext = createContext();

const emptyScan = () => ({
  pk: "",
  id: 0,
  zona: "",
  conteo: "",
  folioconteo: 0,
  almacenExhibidor: "",
  claveArticulo: "",
  descripcion: "",
  medida: 0,
  codigoBarras: "",
  fecha: "",
  usuario: "",
  cantidad: 0,
  factor: 0,
});

const normalizarCodigo = (valor) => {
  const texto = String(valor ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[\u0000-\u001F\u007F\s]/g, "")
    .trim()
    .toUpperCase();
  const sinPrefijo = texto.replace(/^A+/, "");
  return sinPrefijo.replace(/^0+/, "") || "0";
};

export const playBeep = (freq = 800, dur = 100) => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = freq;
    osc.type = "sine";
    gain.gain.value = 0.3;
    osc.start();
    osc.stop(ctx.currentTime + dur / 1000);
  } catch { }
};

// Cede el hilo para que la barra de progreso pinte entre lotes grandes.
const cederHilo = () => new Promise((res) => setTimeout(res, 0));

// Descarga un JSON reportando bytes recibidos (para la barra de %).
// Usa el mismo Bearer que RequestContext (sizesoftx-tenant-access-token).
// Solo se centralizó la lectura del token (misma prioridad tenant||dashboard);
// sin retry, sin refresh y sin cambios al streaming ni al manejo de errores.
const descargarJsonConProgreso = async (url, onBytes) => {
  const token = getAuthorizationToken();
  const resp = await fetch(url, {
    method: "GET",
    headers: { Authorization: "Bearer " + token },
  });
  if (!resp.ok) {
    // Incluir el mensaje del backend (401 tenant, folio inactivo, etc.)
    // para que el toast diga la causa real y no solo el HTTP.
    let causa = `HTTP ${resp.status}`;
    try {
      const texto = await resp.text();
      try {
        const j = JSON.parse(texto);
        causa = j?.message || j?.Message || causa;
      } catch {
        if (texto) causa = `${causa}: ${texto.slice(0, 200)}`;
      }
    } catch { }
    throw new Error(causa);
  }
  const totalBytes = Number(resp.headers.get("content-length") || 0);
  if (!resp.body || typeof resp.body.getReader !== "function") {
    const json = await resp.json();
    try { onBytes?.(1, 1); } catch { }
    return json;
  }
  const reader = resp.body.getReader();
  const partes = [];
  let recibidos = 0;
  let totalLen = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    partes.push(value);
    const n = value?.byteLength || value?.length || 0;
    recibidos += n;
    totalLen += n;
    try { onBytes?.(recibidos, totalBytes); } catch { }
  }
  const plano = new Uint8Array(totalLen);
  let off = 0;
  for (const p of partes) {
    plano.set(p, off);
    off += p.byteLength || p.length || 0;
  }
  const json = JSON.parse(new TextDecoder("utf-8").decode(plano));
  return json;
};

export const ConteosEscaneoProvider = (props) => {
  const { GetRequest, PostRequest, DeleteRequest } = useRequest();
  const { usuario } = useSesion();
  const { handleOpenAlert } = useLayout();
  const navigate = useNavigate();

  // RequestContext recrea sus métodos en cada render. Esta referencia permite
  // consumir siempre la implementación vigente sin cambiar la identidad de los
  // métodos públicos de ConteosEscaneoContext.
  const getRequestRef = useRef(GetRequest);
  const alertRef = useRef(handleOpenAlert);

  useEffect(() => {
    getRequestRef.current = GetRequest;
    alertRef.current = handleOpenAlert;
  }, [GetRequest, handleOpenAlert]);

  const [zona, setZona] = useState("");
  const [conteo, setConteo] = useState("CONTEO 1");
  const [almacenExhibidor, setAlmacenExhibidor] = useState("");
  const [estado, setEstado] = useState("ABIERTO");
  const [scans, setScans] = useState([]);
  const [total, setTotal] = useState(0);
  const [ultimoFolio, setUltimoFolio] = useState(0);
  const [codigoBarras, setCodigoBarras] = useState("");
  const [loading, setLoading] = useState(false);
  const [listaAlmacenes, setListaAlmacenes] = useState([]);
  const [folioInventario, setFolioInventario] = useState(null);
  const [inventarioActivo, setInventarioActivo] = useState(null);
  const [zonas, setZonas] = useState([]);
  const [catalogoArticulos, setCatalogoArticulos] = useState([]);
  const indiceCatalogoRef = useRef(new Map());

  const construirIndice = useCallback((catalogo) => {
    const mapa = new Map();
    for (const articulo of catalogo) {
      const codigo = articulo.codigobarras;
      if (!codigo) continue;
      const normalizado = normalizarCodigo(codigo);
      if (!normalizado || normalizado === "0") continue;
      if (!mapa.has(normalizado)) {
        mapa.set(normalizado, articulo);
      }
    }
    indiceCatalogoRef.current = mapa;
  }, []);

  const handleObtenerAlmacenes = useCallback(async () => {
    const resp = await GetRequest({
      url: apiListaAlmacenesExhibidores,
      loader: "Cargando almacenes...",
    });
    if (!resp.hasError && Array.isArray(resp.result)) {
      setListaAlmacenes(resp.result);
    }
  }, [GetRequest]);

  // Carga del catálogo de la toma a memoria local.
  // Uso manual desde el menú "Catálogo": reporta progreso real por fases
  // (descarga -> procesado -> índice -> guardado) vía onProgress.
  // opciones: { forceRefresh, soloCache, onProgress }
  // - forceRefresh: ignora el caché y descarga todo de nuevo.
  // - soloCache: solo lee localStorage (apertura rápida, sin red).
  // Devuelve el arreglo (compat con llamadas previas).
  const handleCargarCatalogoLocal = useCallback(async (folio, opciones = {}) => {
    const opts = typeof opciones === "boolean" ? { forceRefresh: opciones } : (opciones || {});
    const { forceRefresh = false, soloCache = false, onProgress = null } = opts;
    const informar = (fase, porcentaje, detalle) => {
      try { onProgress?.({ fase, porcentaje, detalle }); } catch { }
    };
    const folioNormalizado = Number(folio);
    if (!Number.isInteger(folioNormalizado) || folioNormalizado <= 0) return [];

    // v3: el catálogo ya no filtra por conteoafijar (el lector es solo
    // índice código->artículo). Se versiona para no reusar cachés viejos.
    const storageKey = `sizesoftx_toma_catalogo_${folioNormalizado}_v3`;
    try {
      Object.keys(localStorage)
        .filter((key) => key.startsWith("sizesoftx_toma_catalogo_") && key !== storageKey)
        .forEach((key) => localStorage.removeItem(key));
    } catch { }

    if (forceRefresh) {
      try { localStorage.removeItem(storageKey); } catch { }
    }

    let catalogoGuardado = [];
    try {
      const almacenado = JSON.parse(localStorage.getItem(storageKey) || "null");
      if (Array.isArray(almacenado) && almacenado.length > 0) {
        // Sin filtro de conteoafijar: el lector es solo índice código->artículo.
        const limpio = almacenado
          .map((a) => ({
            ...a,
            codigobarras: String(a.codigobarras || "").trim(),
            clavearticulo: String(a.clavearticulo || "").trim(),
            descripcion: String(a.descripcion || "").trim(),
          }))
          .filter((a) => !!a.codigobarras);
        catalogoGuardado = limpio;
        setCatalogoArticulos(limpio);
        construirIndice(limpio);
        informar("cache", 100, `${limpio.length} artículos desde caché local`);
        return limpio;
      }
    } catch { }

    // Modo apertura rápida: sin caché no hay red, el usuario lo jala a mano.
    if (soloCache) return [];

    const url = apiConteosEscaneoCatalogoInventario.replace(
      "{folioinventario}",
      String(folioNormalizado)
    );
    let json = null;
    try {
      json = await descargarJsonConProgreso(url, (recibidos, totalBytes) => {
        informar("descarga", totalBytes > 0 ? Math.min(99, Math.round((recibidos / totalBytes) * 100)) : null,
          totalBytes > 0 ? `${(recibidos / 1048576).toFixed(1)} de ${(totalBytes / 1048576).toFixed(1)} MB` : `${(recibidos / 1048576).toFixed(1)} MB descargados`);
      });
      informar("descarga", 100, "Descarga completa");
    } catch (e) {
      try {
        alertRef.current(
          `No se pudo descargar el catálogo de la toma folio ${folioNormalizado}: ${e?.message || "error de red"}`
        );
      } catch { }
      return [];
    }
    // El GET devuelve el JSON crudo del backend (.NET: HasError con mayúscula).
    const traeError = json?.hasError === true || json?.HasError === true;
    if (traeError) {
      try {
        alertRef.current(
          `No se pudo cargar el catálogo de la toma folio ${folioNormalizado}: ${json?.message || json?.Message || "error del servidor"}`
        );
      } catch { }
      return [];
    }
    const catalogoRecibido = Array.isArray(json?.result) ? json.result : [];
    if (catalogoRecibido.length === 0) {
      // El backend explica el vacío (detalle sin cargar vs todo fijado).
      const msgBackend = (json?.message || json?.Message || "").toString();
      const msg = (msgBackend && msgBackend !== "OK") ? msgBackend
        : `La toma folio ${folioNormalizado} no devolvió artículos para escaneo. Verifica que el detalle esté cargado.`;
      try { alertRef.current(msg); } catch { }
      return [];
    }
    // El backend mezcla casings entre endpoints (claveArticulo/codigobarras,
    // codigoBarras, Descripcion, Medida...). Se aceptan todas las variantes,
    // igual que el mapeo de scans en FrmConteosEscaneo.
    const leer = (o, nombres) => {
      for (const n of nombres) {
        const v = o?.[n];
        if (v !== undefined && v !== null && String(v).trim() !== "") return v;
      }
      return undefined;
    };
    // Procesado por lotes para no congelar la UI con ~90 mil registros.
    // Sin filtro de conteoafijar: el lector resuelve cualquier artículo
    // del detalle (código, clave, descripción); el backend valida al guardar.
    const LOTE = 5000;
    const mapeados = [];
    let sinCodigo = 0;
    for (let i = 0; i < catalogoRecibido.length; i += LOTE) {
      const bloque = catalogoRecibido.slice(i, i + LOTE);
      for (const articulo of bloque) {
        const cb = String(leer(articulo, ["codigobarras", "codigoBarras", "CodigoBarras", "sCodigoBarras"]) ?? "").trim();
        if (!cb) { sinCodigo += 1; continue; }
        mapeados.push({
          folioinventario: articulo.folioinventario ?? articulo.FolioInventario ?? folioNormalizado,
          clavearticulo: String(leer(articulo, ["clavearticulo", "claveArticulo", "sClaveArticulo"]) ?? "").trim(),
          descripcion: String(leer(articulo, ["descripcion", "Descripcion", "sDescripcion"]) ?? "").trim(),
          codigobarras: cb,
          codigonormalizado: normalizarCodigo(cb),
          medida: articulo.medida ?? articulo.Medida ?? articulo.dMedida ?? articulo.sMedida ?? 0,
          conteoafijar: Number(articulo.conteoafijar ?? articulo.conteoAFijar ?? articulo.ConteoAFijar ?? 0),
        });
      }
      informar("procesando", Math.round(((i + bloque.length) / catalogoRecibido.length) * 100),
        `${Math.min(i + bloque.length, catalogoRecibido.length)} de ${catalogoRecibido.length} registros`);
      await cederHilo();
    }
    const catalogo = Array.from(new Map(
      mapeados.map((articulo) => [
        `${articulo.codigobarras.toUpperCase()}|${articulo.clavearticulo.toUpperCase()}|${articulo.medida}`,
        articulo,
      ])
    ).values());
    informar("indexando", 50, `Indexando ${catalogo.length} artículos...`);
    await cederHilo();
    if (catalogo.length > 0) {
      setCatalogoArticulos(catalogo);
      construirIndice(catalogo);
      informar("indexando", 100, `${catalogo.length} artículos listos para escaneo`);
      informar("guardando", 50, "Guardando copia local...");
      await cederHilo();
      try {
        const serializado = JSON.stringify(catalogo);
        localStorage.removeItem(storageKey);
        localStorage.setItem(storageKey, serializado);
        informar("guardando", 100, "Copia local guardada");
      } catch {
        // Cuota llena (~90 mil registros superan localStorage): el catálogo
        // queda activo solo en esta sesión; se avisa para recargarlo a mano.
        informar("cache-lleno", 100, "Caché local lleno: el catálogo funciona en esta sesión");
      }
      informar("listo", 100, `${catalogo.length} artículos cargados`);
      return catalogo;
    }
    return catalogoGuardado;
  }, []);

  const resolverArticulo = useCallback((codigoEscaneado) => {
    if (indiceCatalogoRef.current.size === 0) return null;
    const normalizado = normalizarCodigo(codigoEscaneado);
    if (!normalizado || normalizado === "0") return null;
    return indiceCatalogoRef.current.get(normalizado) || null;
  }, []);

  const handleBuscarArticuloLocal = useCallback((codigoBarras) => {
    return resolverArticulo(codigoBarras);
  }, [resolverArticulo]);

  const handleClickRegresarMainDashboard = useCallback(() => {
    navigate("/*");
  }, [navigate]);

  const handleValidarInventarioActivo = useCallback(async () => {
    const resp = await getRequestRef.current({
      url: apiTomaInventarioActivo,
      loader: false,
    });
    if (!resp.hasError && resp.result && resp.result.folioinventario > 0) {
      setInventarioActivo(resp.result);
      return resp.result;
    }
    setInventarioActivo(null);
    return null;
  }, []);

  const handleAbrirConteo = useCallback(async () => {
    if (!zona.trim() || !conteo.trim() || !almacenExhibidor.trim()) {
      handleOpenAlert("Selecciona Zona, Conteo y Almacen/Exhibidor.");
      return;
    }
    const resp = await PostRequest({
      url: apiConteosEscaneoAbrir,
      body: {
        zona: zona.trim().toUpperCase(),
        conteo: conteo.trim().toUpperCase(),
        almacenExhibidor: almacenExhibidor.trim().toUpperCase(),
        usuario: usuario?.usuario || "ADMIN",
      },
      loader: "Abriendo conteo...",
    });
    if (!resp.hasError) {
      setEstado("ABIERTO");
      await handleListarScans();
      await handleObtenerStats();
    }
  }, [zona, conteo, almacenExhibidor, usuario, PostRequest, handleOpenAlert]);

  const handleCerrarConteo = useCallback(async () => {
    const resp = await PostRequest({
      url: apiConteosEscaneoCerrar,
      body: {
        zona: zona.trim().toUpperCase(),
        conteo: conteo.trim().toUpperCase(),
        almacenExhibidor: almacenExhibidor.trim().toUpperCase(),
      },
      alert: true,
      loader: "Cerrando conteo...",
    });
    if (!resp.hasError) {
      setEstado("CERRADO");
      await handleObtenerStats();
    }
  }, [zona, conteo, almacenExhibidor, PostRequest]);

  const handleReiniciarConteo = useCallback(async () => {
    const resp = await PostRequest({
      url: apiConteosEscaneoReiniciar,
      body: {
        zona: zona.trim().toUpperCase(),
        conteo: conteo.trim().toUpperCase(),
        almacenExhibidor: almacenExhibidor.trim().toUpperCase(),
      },
      alert: true,
      loader: "Reiniciando conteo...",
    });
    if (!resp.hasError) {
      setEstado("ABIERTO");
      setScans([]);
      setTotal(0);
      setUltimoFolio(1);
    }
  }, [zona, conteo, almacenExhibidor, PostRequest]);

  const handleListarScans = useCallback(async () => {
    if (!zona.trim() || !conteo.trim() || !almacenExhibidor.trim()) return false;
    const resp = await getRequestRef.current({
      url: apiConteosEscaneoListar +
        "?zona=" + encodeURIComponent(zona.trim().toUpperCase()) +
        "&conteo=" + encodeURIComponent(conteo.trim().toUpperCase()) +
        "&almacen=" + encodeURIComponent(almacenExhibidor.trim().toUpperCase()),
    });
    if (!resp.hasError) {
      setScans(Array.isArray(resp.result) ? resp.result : []);
    }
  }, [zona, conteo, almacenExhibidor]);

  const handleObtenerStats = useCallback(async () => {
    if (!zona.trim() || !conteo.trim() || !almacenExhibidor.trim()) return;
    const resp = await getRequestRef.current({
      url: apiConteosEscaneoStats +
        "?zona=" + encodeURIComponent(zona.trim().toUpperCase()) +
        "&conteo=" + encodeURIComponent(conteo.trim().toUpperCase()) +
        "&almacen=" + encodeURIComponent(almacenExhibidor.trim().toUpperCase()),
    });
    if (!resp.hasError && resp.result) {
      setEstado(resp.result.estado || "ABIERTO");
      setTotal(resp.result.total || 0);
      setUltimoFolio(resp.result.ultimoFolio || 0);
    }
  }, [zona, conteo, almacenExhibidor]);

  const handleObtenerZonas = useCallback(async (folio, almacen) => {
    const folioNormalizado = Number(folio);
    const almacenNormalizado = (almacen || "").trim().toUpperCase();
    if (!Number.isInteger(folioNormalizado) || folioNormalizado <= 0 || !almacenNormalizado) {
      setZonas([]);
      return [];
    }

    const storageKey = `sizesoftx_conteos_zonas_${folioNormalizado}_${encodeURIComponent(almacenNormalizado)}`;
    try {
      const zonasGuardadas = JSON.parse(localStorage.getItem(storageKey) || "[]");
      if (Array.isArray(zonasGuardadas)) setZonas(zonasGuardadas);
    } catch { }

    const resp = await getRequestRef.current({
      url: apiConteosEscaneoZonas.replace("{folioinventario}", String(folioNormalizado))
        + "?almacen=" + encodeURIComponent(almacenNormalizado),
      loader: false,
    });
    const zonasInventario = !resp.hasError && Array.isArray(resp.result) ? resp.result : [];
    setZonas(zonasInventario);
    try {
      localStorage.setItem(storageKey, JSON.stringify(zonasInventario));
    } catch { }
    return zonasInventario;
  }, []);

  const handleCrearScansLote = useCallback(async (codigosBarras) => {
    if (!Array.isArray(codigosBarras) || codigosBarras.length === 0) return null;
    const resp = await PostRequest({
      url: apiConteosEscaneoCrearLote,
      body: {
        zona: zona.trim().toUpperCase(),
        conteo: conteo.trim().toUpperCase(),
        almacenExhibidor: almacenExhibidor.trim().toUpperCase(),
        usuario: usuario?.usuario || "ADMIN",
        folioInventario: Number(folioInventario),
        codigosBarras,
      },
      loader: "Guardando lecturas...",
    });
    if (resp.hasError) return null;
    await handleListarScans();
    await handleObtenerStats();
    await handleObtenerZonas(folioInventario, almacenExhibidor);
    return resp.result;
  }, [zona, conteo, almacenExhibidor, usuario, folioInventario, PostRequest,
    handleListarScans, handleObtenerStats, handleObtenerZonas]);

  const handleObtenerEstado = useCallback(async () => {
    if (!zona.trim() || !conteo.trim() || !almacenExhibidor.trim()) return;
    const resp = await GetRequest({
      url: apiConteosEscaneoEstado +
        "?zona=" + encodeURIComponent(zona.trim().toUpperCase()) +
        "&conteo=" + encodeURIComponent(conteo.trim().toUpperCase()) +
        "&almacen=" + encodeURIComponent(almacenExhibidor.trim().toUpperCase()),
    });
    if (!resp.hasError && resp.result) {
      setEstado(resp.result.estado || "ABIERTO");
    }
  }, [zona, conteo, almacenExhibidor, GetRequest]);

  const handleDisponible = useCallback(async () => {
    if (!zona.trim() || !conteo.trim() || !almacenExhibidor.trim()) return null;
    const resp = await GetRequest({
      url: apiConteosEscaneoDisponible +
        "?zona=" + encodeURIComponent(zona.trim().toUpperCase()) +
        "&conteo=" + encodeURIComponent(conteo.trim().toUpperCase()) +
        "&almacen=" + encodeURIComponent(almacenExhibidor.trim().toUpperCase()),
    });
    if (!resp.hasError && resp.result) {
      return resp.result;
    }
    return null;
  }, [zona, conteo, almacenExhibidor, GetRequest]);

  const handleEliminarConteoCompleto = useCallback(async () => {
    // La zona es opcional (captura sin zona es válida): solo conteo y almacén
    // son obligatorios. Con zona vacía se eliminan las lecturas sin zona.
    if (!conteo.trim() || !almacenExhibidor.trim()) return false;
    const resp = await DeleteRequest({
      url: apiConteosEscaneoEliminarConteoCompleto +
        "?zona=" + encodeURIComponent((zona || "").trim().toUpperCase()) +
        "&conteo=" + encodeURIComponent(conteo.trim().toUpperCase()) +
        "&almacen=" + encodeURIComponent(almacenExhibidor.trim().toUpperCase()),
      alert: true,
      loader: "Eliminando conteo...",
    });
    if (!resp.hasError) {
      setScans([]);
      setTotal(0);
      setUltimoFolio(0);
      setEstado("ABIERTO");
      return true;
    }
    return false;
  }, [zona, conteo, almacenExhibidor, DeleteRequest]);

  const handleObtenerFactor = useCallback(async (claveArticulo, medida) => {
    try {
      const resp = await GetRequest({
        url: apiBuscarArticuloPorClaveMedida
          .replace("{clavearticulo}", encodeURIComponent(claveArticulo))
          .replace("{medida}", encodeURIComponent(medida || 0)),
      });
      if (!resp.hasError && resp.result) {
        return resp.result.factor || 1;
      }
    } catch { }
    return 1;
  }, [GetRequest]);

  const handleBuscarArticuloEnInventarioActivo = useCallback(async (codigoBarras) => {
    
    const folio = Number(folioInventario);
    if (!codigoBarras?.trim() || !Number.isInteger(folio) || folio <= 0) {
      return null;
    }
    const resp = await GetRequest({
      url: apiBuscarArticulosInventario
        .replace("{folioinventario}", String(folio))
        .replace("{codigobarras}", encodeURIComponent(codigoBarras.trim())),
    });
    if (resp.hasError || !resp.result) {
      return null;
    }
    const articulo = resp.result;
    const factor = await handleObtenerFactor(
      articulo.clavearticulo || articulo.sClaveArticulo || "",
      articulo.medida || articulo.dMedida || 0
    );
    return {
      clavearticulo: articulo.clavearticulo || articulo.sClaveArticulo || "",
      descripcion: articulo.descripcion || articulo.sDescripcion || "",
      medida: articulo.medida || articulo.dMedida || 0,
      codigobarras: articulo.codigobarras || articulo.sCodigoBarras || codigoBarras,
      factor,
    };
  }, [folioInventario, GetRequest, handleObtenerFactor]);

  const handleCrearScan = useCallback(async () => {
    const code = codigoBarras.trim();
    if (!code) {
      handleOpenAlert("Ingresa un código de barras.");
      return null;
    }
    setLoading(true);
    try {
      const respArticulo = await GetRequest({
        url: apiBuscarArticuloPorCodigoBarras.replace("{codigobarras}", encodeURIComponent(code)),
      });
      if (respArticulo.hasError || !respArticulo.result) {
        handleOpenAlert("Articulo no encontrado para el código: " + code);
        return null;
      }
      const art = respArticulo.result;
      const clave = art.clavearticulo || art.claveArticulo || "";
      const medida = art.medida || 0;
      const factor = await handleObtenerFactor(clave, medida);
      const resp = await PostRequest({
        url: apiConteosEscaneoCrear,
        body: {
          zona: zona.trim().toUpperCase(),
          conteo: conteo.trim().toUpperCase(),
          almacenExhibidor: almacenExhibidor.trim().toUpperCase(),
          claveArticulo: clave,
          descripcion: art.descripcion || "",
          medida,
          codigoBarras: code,
          usuario: usuario?.usuario || "ADMIN",
          folioInventario,
          factor,
        },
        loader: "Registrando escaneo...",
      });
      if (!resp.hasError) {
        playBeep(800, 120);
        setCodigoBarras("");
        await handleListarScans();
        await handleObtenerStats();
        return resp.result;
      }
      playBeep(200, 300);
      handleOpenAlert(resp.message || "Error al registrar escaneo");
    } catch (e) {
      handleOpenAlert("Error inesperado al escanear");
    } finally {
      setLoading(false);
    }
    return null;
  }, [codigoBarras, zona, conteo, almacenExhibidor, usuario, folioInventario, GetRequest, PostRequest, handleOpenAlert, handleListarScans, handleObtenerStats, handleObtenerFactor]);

  const handleCrearScanDesdeArticulo = useCallback(async (articulo) => {
    if (!articulo) return null;
    setLoading(true);
    try {
      const clave = articulo.clavearticulo || articulo.claveArticulo || "";
      const medida = articulo.medida || 0;
      const factor = await handleObtenerFactor(clave, medida);
      const resp = await PostRequest({
        url: apiConteosEscaneoCrear,
        body: {
          zona: zona.trim().toUpperCase(),
          conteo: conteo.trim().toUpperCase(),
          almacenExhibidor: almacenExhibidor.trim().toUpperCase(),
          claveArticulo: clave,
          descripcion: articulo.descripcion || "",
          medida,
          codigoBarras: articulo.codigoBarras || articulo.codigobarras || "",
          usuario: usuario?.usuario || "ADMIN",
          folioInventario,
          factor,
        },
        loader: "Registrando escaneo...",
      });
      if (!resp.hasError) {
        playBeep(800, 120);
        setCodigoBarras("");
        await handleListarScans();
        await handleObtenerStats();
        return resp.result;
      }
      playBeep(200, 300);
      handleOpenAlert(resp.message || "Error al registrar escaneo desde artículo");
    } catch (e) {
      handleOpenAlert("Error inesperado al registrar artículo");
    } finally {
      setLoading(false);
    }
    return null;
  }, [zona, conteo, almacenExhibidor, usuario, folioInventario, PostRequest, handleOpenAlert, handleListarScans, handleObtenerStats, handleObtenerFactor]);

  const handleEliminarScan = useCallback(async (pk) => {
    if (!pk) return false;
    setLoading(true);
    try {
      const resp = await DeleteRequest({
        url: apiConteosEscaneoEliminar.replace("{pk}", encodeURIComponent(pk)),
        alert: true,
      });
      if (!resp.hasError) {
        await handleListarScans();
        await handleObtenerStats();
        return true;
      }
      return false;
    } finally {
      setLoading(false);
    }
  }, [DeleteRequest, handleListarScans, handleObtenerStats]);

  const handleCambiarZona = useCallback((val) => {
    setZona(val);
    setScans([]);
    setTotal(0);
    setUltimoFolio(0);
    setEstado("ABIERTO");
  }, []);

  const handleCambiarConteo = useCallback((val) => {
    setConteo(val);
    setScans([]);
    setTotal(0);
    setUltimoFolio(0);
    setEstado("ABIERTO");
  }, []);

  const handleCambiarAlmacen = useCallback((val) => {
    setAlmacenExhibidor(val);
    setScans([]);
    setTotal(0);
    setUltimoFolio(0);
    setEstado("ABIERTO");
  }, []);

  const recargarDatos = useCallback(async () => {
    if (!zona.trim() || !conteo.trim() || !almacenExhibidor.trim()) return;
    await handleListarScans();
    await handleObtenerStats();
  }, [zona, conteo, almacenExhibidor, handleListarScans, handleObtenerStats]);

  const handleCargarConteo = useCallback(async () => {
    if (!zona.trim() || !conteo.trim() || !almacenExhibidor.trim()) {
      handleOpenAlert("Selecciona Zona, Conteo y Almacen/Exhibidor.");
      return;
    }
    setLoading(true);
    try {
      const resp = await PostRequest({
        url: apiConteosEscaneoAbrir,
        body: {
          zona: zona.trim().toUpperCase(),
          conteo: conteo.trim().toUpperCase(),
          almacenExhibidor: almacenExhibidor.trim().toUpperCase(),
          usuario: usuario?.usuario || "ADMIN",
        },
        loader: "Abriendo conteo...",
      });
      if (!resp.hasError) {
        setEstado("ABIERTO");
        await recargarDatos();
      }
    } finally {
      setLoading(false);
    }
  }, [zona, conteo, almacenExhibidor, usuario, PostRequest, handleOpenAlert, recargarDatos]);

  // "Nuevo" = nueva zona/conteo dentro de LA MISMA toma activa.
  // Conserva folio, almacén, zonas y catálogo: si se limpiaran, el siguiente
  // guardado mandaría folio 0 y el backend lo rechaza en silencio.
  const handleNuevoConteo = useCallback(() => {
    setZona("");
    setConteo("CONTEO 1");
    setEstado("ABIERTO");
    setScans([]);
    setTotal(0);
    setUltimoFolio(0);
    setCodigoBarras("");
  }, []);

  return (
    <ConteosEscaneoContext.Provider
      value={{
        zona, conteo, almacenExhibidor,
        estado, scans, total, ultimoFolio,
        codigoBarras, loading, listaAlmacenes,
        folioInventario, inventarioActivo, zonas, catalogoArticulos,
        setCodigoBarras, setFolioInventario,
        handleCambiarZona, handleCambiarConteo, handleCambiarAlmacen,
        handleCargarConteo, handleAbrirConteo, handleCerrarConteo,
        handleReiniciarConteo, handleListarScans, handleObtenerStats,
        handleObtenerEstado, handleCrearScan, handleCrearScanDesdeArticulo,
        handleEliminarScan, handleObtenerAlmacenes, handleNuevoConteo, recargarDatos,
        handleDisponible, handleEliminarConteoCompleto,
        handleClickRegresarMainDashboard, handleValidarInventarioActivo,
        handleObtenerZonas,
        handleCargarCatalogoLocal, handleBuscarArticuloLocal, handleCrearScansLote,
        handleBuscarArticuloEnInventarioActivo,
        resolverArticulo,
      }}
    >
      {props.children}
    </ConteosEscaneoContext.Provider>
  );
};

export const useConteosEscaneo = () => useContext(ConteosEscaneoContext);
